<template>
    <div>

        <v-dialog max-width="900"
                  persistent
                  v-model="value">
            <toolbar-page title="Entry link templates" icon="mdi-link">
                <template slot="buttons">

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn fab x-small color="primary" v-on="on" @click="create">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        <span>Create new template</span>
                    </v-tooltip>

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click="close">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Close</span>
                    </v-tooltip>

                </template>
            </toolbar-page>

            <v-card>
                <v-card-text>

                    <v-data-table :loading="loading"
                                  :headers="headers"
                                  :items="entryLinks"
                    >
                        <template v-slot:body="{ items }">
                            <tbody>

                            <!-- NO DATA INFO -->
                            <tr v-if="items.length===0 && !loading">
                                <td colspan="99">
                                    <v-alert
                                            color="primary"
                                            class="mt-4 body-2"
                                            icon="mdi-information">
                                        No links found. Click on the plus icon to create one.
                                    </v-alert>
                                </td>
                            </tr>

                            <!-- ITEMS -->
                            <tr v-for="link in items" :key="link.id">
                                <td @click="select(link)" class="cursor-pointer">
                                    {{link.name}}
                                </td>
                                <td>
                                    <v-layout justify-center>
                                        <v-icon v-if="link.hash_algo!=='None'" color="green lighten-1">
                                            mdi-shield-check
                                        </v-icon>
                                        <v-icon v-else color="red lighten-2">mdi-shield-alert</v-icon>
                                    </v-layout>
                                </td>
                                <td>
                                    <v-layout justify-center>
                                        <v-icon v-if="link.params.length > 0" color="green lighten-1">mdi-check</v-icon>
                                        <v-icon v-else color="grey">mdi-close</v-icon>
                                    </v-layout>
                                </td>
                                <td>
                                    <v-btn color="primary" @click="select(link)" small>Select</v-btn>
                                    <v-btn color="primary" @click="edit(link)" small>Edit</v-btn>
                                    <v-btn icon @click="deleteLink(link)">
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            </tbody>
                        </template>
                    </v-data-table>
                </v-card-text>

                <json v-if="debug" :data="entryLinks"></json>
            </v-card>
        </v-dialog>

        <edit-link-dialog v-model="editLinkModel"
                          :data-variables="dataVariables"
                          :key="editKey"
                          :edit-template="editTemplate"
                          :link="link"
                          @use="useClicked">
        </edit-link-dialog>

        <confirmation-dialog delete
                             headline="Delete link?"
                             :description="deleteConfirmation"
                             v-model="deleteDialogModel"
                             confirm-button-text="Delete"
                             abort-button-text="Cancel"
                             @selected="deleteLinkDo">
        </confirmation-dialog>
    </div>
</template>

<script>
    import EditLinkDialog from "./EditLinkDialog";
    import ToolbarPage from "../../../components/UI/ToolbarPage";
    import DataVariablePicker from "../../../components/UI/DataVariablePicker";
    import {mapActions, mapState} from 'vuex';
    import TitleCard from "../../../components/UI/TitleCard";
    import TitleCardList from "../../../components/UI/TitleCardList";
    import {v4 as uuid} from "uuid";
    import Store from "../store";
    import Json from 'vue-json-pretty';
    import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";

    const vuexModule = "LinksManager";

    export default {
        name: "LinksManager",

        components: {
            ToolbarPage,
            DataVariablePicker,
            TitleCard,
            TitleCardList,
            Json,
            EditLinkDialog,
            ConfirmationDialog
        },

        props: {
            value: {
                type: Boolean,
                required: true
            }
        },

        data() {
            return {
                debug: false,
                editLinkModel: false,
                headers: [

                    {text: 'TEMPLATE NAME', align: 'left', sortable: false, width: '100%', value: 'reward.name'},
                    {text: 'SECURE', align: 'center', sortable: false, value: 'id', width: '100px'},
                    {text: 'PIPES DATA', align: 'center ', sortable: false, value: 'id', width: '100px'},
                    {text: 'ACTIONS', sortable: false, align: 'left', width: '220px'},
                ],
                link: {},
                editKey: uuid(),
                editTemplate: false,
                deleteDialogModel: false,
                deleteLinkId: null,
                deleteConfirmation:
                    'Are you sure you want to delete this template? '+
                    'This cannot be undone. Projects currently using this template will NOT be affected!'
            }
        },

        computed: {
            ...mapState(['dataVariables']),

            ...mapState(vuexModule, [
                'entryLinks',
                'loading'
            ]),
        },

        methods: {
            ...mapActions(['getDataVariables']),

            ...mapActions(vuexModule, [
                'getEntryLinks',
                'deleteEntryLink'
            ]),

            useClicked(data) {
                this.close();
                this.$emit('use', data);
            },

            close() {
                this.$emit('input', false);
            },

            edit(link) {
                this.editKey = uuid();
                this.link = link;
                this.editLinkModel = true;
                this.editTemplate = true;
            },

            select(link) {
                this.editKey = uuid();
                this.link = link;
                this.$emit('input', false);
                this.editLinkModel = false;
                this.editTemplate = false;
                this.$emit('use', link);
            },

            create() {
                this.editKey = uuid();
                this.link = null;
                this.editLinkModel = true;
                this.editTemplate = true;
            },

            deleteLink(link) {
                this.deleteLinkId = link.id;
                this.deleteDialogModel = true;
            },

            deleteLinkDo() {
                this.deleteEntryLink(this.deleteLinkId)
                    .then(() => {
                        Bus.$emit('flash-message', {type: 'success', message: 'Template deleted!'});
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {type: 'error', message: 'OOPS! Failed loading links!'});
                    });
            }
        },

        mounted() {

            this.getDataVariables()
                .catch(error => {

                    console.error(error);

                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! We failed to load data variables. Try reloading!'
                    });
                });

            this.getEntryLinks(true)
                .catch((error) => {
                    console.error(error);
                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS! Failed loading links!'});
                });
        },

        created() {
            if (!this.$store.state[vuexModule]) {
                this.$store.registerModule(vuexModule, Store);
            }
        },
    }
</script>
